import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import CustomTableGrid from "../../../../customComponents/CustomTableGrid/CustomTableGrid";
import CustomFilterComponent from "../../../../customComponents/CustomFilterComponent";
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import "../index.css";
import "./reportsAndAnalysis.css";
import {
  MOCK_PO_FILL_RATES,
  MOCK_PRODUCTION_BATCHES,
  MOCK_OUTLET_METRICS,
  MOCK_WASTAGE_LOG,
  MOCK_INVOICES,
  MOCK_REPORT_SUMMARY_STATS,
  MOCK_LOCATION_OPTIONS,
} from "../MOCK_DATA_KOS";

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = ["PO Fill Rates", "Production", "Outlet Perf.", "Wastage Cost", "Inter-org Billing"];

const FILTER_CONFIG = [
  {
    key: "timeframe",
    label: "Timeframe",
    options: [
      { label: "Today", value: "today" },
      { label: "This Week", value: "this_week" },
      { label: "This Month", value: "this_month" },
      { label: "Last Month", value: "last_month" },
    ],
  },
  {
    key: "location",
    label: "Location",
    options: MOCK_LOCATION_OPTIONS,
  },
];

const EMPTY_FILTERS = { timeframe: [], location: [] };

// ─── Badge Helpers ────────────────────────────────────────────────────────────

function FillRateBadge({ value }) {
  const cls =
    value >= 95 ? "rAa_kos_fill-green" : value >= 80 ? "rAa_kos_fill-yellow" : "rAa_kos_fill-red";
  return <span className={`rAa_kos_fill-badge ${cls}`}>{value}%</span>;
}
FillRateBadge.propTypes = { value: PropTypes.number.isRequired };

function QcStatusCell({ status }) {
  return (
    <span className={status === "Passed" ? "rAa_kos_qc-passed" : "rAa_kos_qc-warning"}>
      {status}
    </span>
  );
}
QcStatusCell.propTypes = { status: PropTypes.string.isRequired };

function CategoryCell({ category }) {
  const cls = `rAa_kos_category-${category.toLowerCase()}`;
  return <span className={cls}>{category}</span>;
}
CategoryCell.propTypes = { category: PropTypes.string.isRequired };

function InvoiceStatusBadge({ status }) {
  const cls = `rAa_kos_status-${status.toLowerCase()}`;
  return <span className={`rAa_kos_invoice-badge ${cls}`}>{status}</span>;
}
InvoiceStatusBadge.propTypes = { status: PropTypes.string.isRequired };

// ─── Column Definitions ───────────────────────────────────────────────────────

const PO_COLUMNS = [
  { key: "id", label: "PO NUMBER", render: (row) => <span className="rAa_kos_id-link">{row.id}</span> },
  { key: "outlet", label: "OUTLET" },
  { key: "requestedQty", label: "REQUESTED QTY" },
  { key: "dispatchedQty", label: "DISPATCHED QTY" },
  { key: "fillRate", label: "FILL RATE", render: (row) => <FillRateBadge value={row.fillRate} /> },
  { key: "date", label: "DATE" },
];

const PRODUCTION_COLUMNS = [
  { key: "id", label: "BATCH ID", render: (row) => <span className="rAa_kos_id-link">{row.id}</span> },
  { key: "item", label: "ITEM" },
  { key: "inputKg", label: "INPUT (KG)" },
  { key: "outputKg", label: "OUTPUT (KG)" },
  { key: "yieldPct", label: "YIELD %", render: (row) => <FillRateBadge value={row.yieldPct} /> },
  { key: "qcStatus", label: "QC STATUS", render: (row) => <QcStatusCell status={row.qcStatus} /> },
];

const OUTLET_COLUMNS = [
  { key: "outlet", label: "OUTLET NAME" },
  { key: "posPlaced", label: "POS PLACED" },
  { key: "itemsRequested", label: "ITEMS REQUESTED" },
  { key: "fillRate", label: "FILL RATE", render: (row) => <FillRateBadge value={row.fillRate} /> },
  {
    key: "returnsRejects",
    label: "RETURNS/REJECTS",
    render: (row) => (
      <span className={row.returnsRejects > 10 ? "rAa_kos_high-rejects" : ""}>{row.returnsRejects}</span>
    ),
  },
];

const WASTAGE_COLUMNS = [
  { key: "item", label: "ITEM" },
  { key: "category", label: "CATEGORY", render: (row) => <CategoryCell category={row.category} /> },
  { key: "qtyWasted", label: "QTY WASTED" },
  { key: "reason", label: "REASON" },
  { key: "costImpact", label: "COST IMPACT", render: (row) => <span className="rAa_kos_cost-impact">{row.costImpact}</span> },
];

const INVOICE_COLUMNS = [
  { key: "id", label: "INVOICE #", render: (row) => <span className="rAa_kos_id-link">{row.id}</span> },
  { key: "outlet", label: "OUTLET" },
  { key: "period", label: "PERIOD" },
  { key: "amount", label: "AMOUNT" },
  { key: "status", label: "STATUS", render: (row) => <InvoiceStatusBadge status={row.status} /> },
  { key: "action", label: "ACTION", render: () => <button className="rAa_kos_action-link">View</button> },
];

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, valueClass, alert }) {
  return (
    <div className={`rAa_kos_stat-card${alert ? " rAa_kos_stat-card-alert" : ""}`}>
      <p className="rAa_kos_stat-label">{label}</p>
      <p className={`rAa_kos_stat-value${valueClass ? ` ${valueClass}` : ""}`}>{value}</p>
      {sub && <p className="rAa_kos_stat-sub">{sub}</p>}
    </div>
  );
}
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  sub: PropTypes.string,
  valueClass: PropTypes.string,
  alert: PropTypes.bool,
};

// ─── Tab Views ────────────────────────────────────────────────────────────────

function PoFillRatesTab() {
  const s = MOCK_REPORT_SUMMARY_STATS.poFillRates;
  return (
    <>
      <div className="rAa_kos_stat-cards">
        <StatCard label="Average Fill Rate"   value={s.avgFillRate}       sub={`${s.avgFillRateDelta} this week`} />
        <StatCard label="Total POs Fulfilled" value={s.totalPosFulfilled} sub={`Across ${s.outletCount} outlets`} />
        <StatCard label="Low Fill Rate Alerts" value={s.lowFillRateAlerts} sub="POs below 80% threshold" valueClass="rAa_kos_stat-value-red" alert />
      </div>
      <div className="rAa_kos_table-card">
        <div className="rAa_kos_table-header">
          <div>
            <p className="rAa_kos_table-title">Purchase Order Fill Rates</p>
            <p className="rAa_kos_table-subtitle">Dispatched vs Requested quantities per PO</p>
          </div>
        </div>
        <div className="rAa_kos_table-scroll">
          <CustomTableGrid
            columns={PO_COLUMNS}
            rows={MOCK_PO_FILL_RATES}
            rowKey="id"
            gridTemplateColumns="200px minmax(140px,1fr) 140px 150px 110px 100px"
          />
        </div>
      </div>
    </>
  );
}

function ProductionTab() {
  const s = MOCK_REPORT_SUMMARY_STATS.production;
  return (
    <>
      <div className="rAa_kos_stat-cards">
        <StatCard label="Average Yield Rate"    value={s.avgYieldRate}      sub={`${s.avgYieldRateDelta} vs target`} />
        <StatCard label="Total Output (kg)"     value={s.totalOutputKg}     sub={`Across ${s.activeBatches} active batches`} />
        <StatCard label="Production Waste (kg)" value={s.productionWasteKg} sub={`${s.wastePercentage} of total raw materials`} valueClass="rAa_kos_stat-value-red" />
      </div>
      <div className="rAa_kos_table-card">
        <div className="rAa_kos_table-header">
          <div>
            <p className="rAa_kos_table-title">Recent Batches Yield</p>
          </div>
        </div>
        <div className="rAa_kos_table-scroll">
          <CustomTableGrid
            columns={PRODUCTION_COLUMNS}
            rows={MOCK_PRODUCTION_BATCHES}
            rowKey="id"
            gridTemplateColumns="120px minmax(140px,1fr) 110px 120px 100px 120px"
          />
        </div>
      </div>
    </>
  );
}

function OutletPerfTab() {
  const s = MOCK_REPORT_SUMMARY_STATS.outletPerf;
  return (
    <>
      <div className="rAa_kos_stat-cards">
        <StatCard label="Top Outlet"          value={s.topOutlet}      sub={`${s.topOutletFillRate} Fill Rate \u2022 ${s.topOutletRejections} Rejections`} />
        <StatCard label="Needs Attention"     value={s.needsAttention} sub={`${s.needsAttentionFillRate} Fill Rate \u2022 ${s.needsAttentionMissed} Missed Deliveries`} valueClass="rAa_kos_stat-value-red" />
        <StatCard label="Average Order Value" value={s.avgOrderValue}  sub="Per outlet/day" />
      </div>
      <div className="rAa_kos_table-card">
        <div className="rAa_kos_table-header">
          <div>
            <p className="rAa_kos_table-title">Outlet Metrics</p>
          </div>
        </div>
        <div className="rAa_kos_table-scroll">
          <CustomTableGrid
            columns={OUTLET_COLUMNS}
            rows={MOCK_OUTLET_METRICS}
            rowKey="outlet"
            gridTemplateColumns="minmax(150px,1fr) 120px 160px 110px 150px"
          />
        </div>
      </div>
    </>
  );
}

function WastageCostTab() {
  const s = MOCK_REPORT_SUMMARY_STATS.wastageCost;
  return (
    <>
      <div className="rAa_kos_stat-cards">
        <StatCard label="Total Waste Cost" value={s.totalWasteCost}  sub="This Week" valueClass="rAa_kos_stat-value-red" alert />
        <StatCard label="Top Wasted Item"  value={s.topWastedItem}   sub={`${s.topWastedReason} \u2022 ${s.topWastedQty} (${s.topWastedCost})`} />
        <StatCard label="Waste % of Cost"  value={s.wastePctOfCost}  sub={`${s.wastePctDelta} vs last week (Improved)`} />
      </div>
      <div className="rAa_kos_table-card">
        <div className="rAa_kos_table-header">
          <div>
            <p className="rAa_kos_table-title">Wastage Log</p>
          </div>
        </div>
        <div className="rAa_kos_table-scroll">
          <CustomTableGrid
            columns={WASTAGE_COLUMNS}
            rows={MOCK_WASTAGE_LOG}
            rowKey="item"
            gridTemplateColumns="minmax(130px,1fr) 120px 120px 180px 130px"
          />
        </div>
      </div>
    </>
  );
}

function InterOrgBillingTab() {
  const s = MOCK_REPORT_SUMMARY_STATS.interOrgBilling;
  return (
    <>
      <div className="rAa_kos_stat-cards">
        <StatCard label="Total Billed MTD"     value={s.totalBilledMTD}      sub={s.billingPeriod} />
        <StatCard label="Outstanding Invoices" value={s.outstandingInvoices}  sub={`Across ${s.outstandingInvoiceCount} invoices`} valueClass="rAa_kos_stat-value-orange" />
        <StatCard label="Settled"              value={s.settled}              sub={`${s.collectionRate} Collection Rate`} valueClass="rAa_kos_stat-value-green" />
      </div>
      <div className="rAa_kos_table-card">
        <div className="rAa_kos_table-header">
          <div>
            <p className="rAa_kos_table-title">Recent Invoices</p>
          </div>
          <PosCustomButton
            title="Generate Statements"
            variant="black-s"
            p="7px 14px"
            fontSize="13px"
            border
          />
        </div>
        <div className="rAa_kos_table-scroll">
          <CustomTableGrid
            columns={INVOICE_COLUMNS}
            rows={MOCK_INVOICES}
            rowKey="id"
            gridTemplateColumns="160px minmax(140px,1fr) 160px 120px 130px 90px"
          />
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReportsAndAnalysisKitchenOS() {
  const [activeTab, setActiveTab] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);

  const activeFilterCount = useMemo(
    () => Object?.values(selectedFilters)?.reduce((sum, arr) => sum + arr?.length, 0),
    [selectedFilters]
  );

  return (
    <Navbar pageTitle="Kitchen OS">
      <div className="idx_kos_wrapper">
        <div className="rAa_kos_page">
          {/* Header */}
          <div className="rAa_kos_header">
            <div>
              <div className="rAa_kos_title-row">
                <ChartBarIcon className="rAa_kos_header-icon" />
                <p className="rAa_kos_title">Reports &amp; Analytics</p>
              </div>
              <p className="rAa_kos_subtitle">Kitchen performance, fill rates, and wastage metrics.</p>
            </div>
            <div className="rAa_kos_toolbar">
              <div className="rAa_kos_filter-btn-wrapper">
                <PosCustomButton
                  title="Filter"
                  variant="black-s"
                  p="7px 14px"
                  fontSize="13px"
                  border
                  onClickFunction={() => setFilterOpen(true)}
                />
                {activeFilterCount > 0 && (
                  <span className="rAa_kos_filter-badge">{activeFilterCount}</span>
                )}
              </div>
              <PosCustomButton
                title="Export All"
                variant="black-s"
                p="7px 14px"
                fontSize="13px"
                border
                iconOnLeft={<ArrowDownTrayIcon style={{ width: 15, height: 15 }} />}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="rAa_kos_tabs">
            {TABS?.map((tab, i) => (
              <button
                key={tab}
                className={`rAa_kos_tab ${activeTab === i ? "rAa_kos_tab-active" : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 0 && <PoFillRatesTab />}
          {activeTab === 1 && <ProductionTab />}
          {activeTab === 2 && <OutletPerfTab />}
          {activeTab === 3 && <WastageCostTab />}
          {activeTab === 4 && <InterOrgBillingTab />}
        </div>
      </div>

      <CustomFilterComponent
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        filterConfig={FILTER_CONFIG}
        selectedFilters={selectedFilters}
        onApplyFilter={setSelectedFilters}
        variant="panel"
      />
    </Navbar>
  );
}
